import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Truck, Mail, Lock, Unlock } from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { resolveApiErrorMessage } from '../../../../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type AgentRow = {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function StatusPill({ status }: { status: string }) {
  const isActive = status === 'ACTIVE';
  return (
    <span
      className={
        isActive
          ? 'inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200'
          : 'inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200'
      }
    >
      {isActive ? 'Active' : status.toLowerCase()}
    </span>
  );
}

function WorkspaceShippingAgentsPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/admin/shipping-agents`);
      if (!res.ok) {
        throw new Error(
          await resolveApiErrorMessage(res, 'Unable to load shipping agents.'),
        );
      }
      const data = (await res.json()) as AgentRow[];
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to load shipping agents.',
      );
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/auth/admin/shipping-agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          fullName: fullName.trim() || undefined,
          phone: phone.trim() || undefined,
        }),
      });
      if (!res.ok) {
        throw new Error(
          await resolveApiErrorMessage(res, 'Unable to add the agent.'),
        );
      }
      const data = (await res.json()) as { email: string; roleAttached: boolean };
      setSuccess(
        data.roleAttached
          ? `${data.email} has been invited. They will receive an email with the sign-in link.`
          : `${data.email} has been invited, but no "Shipping agent" role was found. Assign permissions from the Roles page so they can mark deliveries.`,
      );
      setEmail('');
      setFullName('');
      setPhone('');
      setShowForm(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add the agent.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (row: AgentRow) => {
    const next = row.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
    setStatusBusyId(row.id);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/auth/admin/shipping-agents/${row.id}/status`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: next }),
        },
      );
      if (!res.ok) {
        throw new Error(
          await resolveApiErrorMessage(res, 'Unable to update the agent.'),
        );
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update the agent.');
    } finally {
      setStatusBusyId(null);
    }
  };

  const stats = useMemo(() => {
    const active = agents.filter((a) => a.status === 'ACTIVE').length;
    return { total: agents.length, active };
  }, [agents]);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="mb-1 flex items-center gap-2 workspace-page-title">
              <Truck className="h-6 w-6 text-brand-600" strokeWidth={2} />
              Shipping agents
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              Add a delivery agent and we will email them a sign-in link. They sign in with email + a 6-digit code we send each time — no password to remember.
            </p>
          </div>
          <div className="text-xs text-slate-500">
            {stats.active}/{stats.total} active
          </div>
        </section>

        <section className="glass-panel max-w-3xl p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="mb-1 text-sm font-semibold text-slate-900">
                Add a shipping agent
              </h2>
              <p className="text-xs text-slate-500">
                Email is the only thing we really need. We will send the welcome message and then the sign-in code on every login.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm((p) => !p);
                setError(null);
                setSuccess(null);
              }}
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500"
            >
              {showForm ? 'Hide form' : 'Add agent'}
            </button>
          </div>

          {error && (
            <p className="mt-3 text-xs text-red-600">{error}</p>
          )}
          {success && (
            <p className="mt-3 text-xs text-emerald-700">{success}</p>
          )}

          {showForm && (
            <form className="mt-4 grid gap-3 sm:grid-cols-2" onSubmit={handleCreate}>
              <label className="space-y-1 text-sm sm:col-span-2">
                <span className="block text-xs font-medium text-slate-700">
                  Email <span className="text-red-500">*</span>
                </span>
                <input
                  type="email"
                  required
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="block text-xs font-medium text-slate-700">
                  Full name (optional)
                </span>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="block text-xs font-medium text-slate-700">
                  Phone (optional)
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 0100"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2.25} />
                  {submitting ? 'Sending invite…' : 'Send invite email'}
                </button>
              </div>
            </form>
          )}
        </section>

        <section className="glass-panel max-w-5xl overflow-hidden">
          <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50/60 px-5 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Agents</h2>
            <button
              type="button"
              onClick={() => void reload()}
              className="text-xs font-medium text-brand-600 hover:text-brand-500"
            >
              Refresh
            </button>
          </header>

          {loading ? (
            <p className="px-5 py-8 text-center text-xs text-slate-500">Loading…</p>
          ) : agents.length === 0 ? (
            <p className="px-5 py-8 text-center text-xs text-slate-500">
              No shipping agents yet. Add one above to get started.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Agent</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Created</th>
                    <th className="px-5 py-3 font-medium">Last login</th>
                    <th className="px-5 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agents.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3 align-top">
                        <div className="font-medium text-slate-900">
                          {row.fullName || row.email}
                        </div>
                        {row.fullName && (
                          <div className="text-xs text-slate-500">{row.email}</div>
                        )}
                        {row.phone && (
                          <div className="text-xs text-slate-500">{row.phone}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 align-top">
                        <StatusPill status={row.status} />
                      </td>
                      <td className="px-5 py-3 align-top text-xs text-slate-600">
                        {formatDate(row.createdAt)}
                      </td>
                      <td className="px-5 py-3 align-top text-xs text-slate-600">
                        {formatDate(row.lastLoginAt)}
                      </td>
                      <td className="px-5 py-3 align-top text-right">
                        <button
                          type="button"
                          onClick={() => void toggleStatus(row)}
                          disabled={statusBusyId === row.id}
                          className={
                            row.status === 'ACTIVE'
                              ? 'inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60'
                              : 'inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60'
                          }
                        >
                          {row.status === 'ACTIVE' ? (
                            <>
                              <Lock className="h-3 w-3" strokeWidth={2.25} />
                              Block
                            </>
                          ) : (
                            <>
                              <Unlock className="h-3 w-3" strokeWidth={2.25} />
                              Activate
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

export default WorkspaceShippingAgentsPage;
